import {Database} from '@tnc/shared';
import {ApexOptions} from 'apexcharts';
import {useMemo} from 'react';
import ReactApexChart from 'react-apexcharts';

type MarketDepthDataT = {
  price: number;
  quantity: number;
  cumulative_quantity: number;
  order_type: Database['public']['Enums']['position_exchange_order_types'];
};

type MarketDepthChartPropsT = {
  data: MarketDepthDataT[];
};

function MarketDepthChart({data}: MarketDepthChartPropsT) {
  const {buyData, sellData} = useMemo(() => {
    const buyOrders = data
      .filter(order => order.order_type === 'buy')
      .sort((a, b) => b.price - a.price)
      .map((order, index, array) => ({
        x: order.price,
        y: array[index].cumulative_quantity,
      }));

    const sellOrders = data
      .filter(order => order.order_type === 'sell' || order.order_type === 'wholesale')
      .sort((a, b) => a.price - b.price)
      .map((order, index, array) => ({
        x: order.price,
        y: array[index].cumulative_quantity,
      }));

    if (sellOrders.length === 1) {
      return {
        buyData: buyOrders,
        sellData: [
          {x: sellOrders[0].x, y: 0},
          {x: sellOrders[0].x, y: sellOrders[0].y},
        ],
      };
    }

    return {buyData: buyOrders, sellData: sellOrders};
  }, [data]);

  const options: ApexOptions = {
    chart: {
      height: 350,
      type: 'line',
      toolbar: {
        show: false,
      },
    },
    stroke: {
      curve: 'stepline',
      width: 2,
    },
    grid: {
      borderColor: '#f2f5f7',
    },
    title: {
      text: 'Market Depth',
      align: 'left',
      style: {
        fontSize: '13px',
        fontWeight: 'bold',
        color: '#8c9097',
      },
    },
    xaxis: {
      type: 'numeric',
      title: {
        text: 'Price',
      },
      labels: {
        style: {
          colors: '#8c9097',
          fontSize: '11px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      title: {
        text: 'Quantity',
      },
      min: 0,
      labels: {
        style: {
          colors: '#8c9097',
          fontSize: '11px',
          fontWeight: 600,
        },
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
    },
  };

  const series = [
    {
      name: 'Buy Orders',
      data: buyData,
      color: '#22c55e',
    },
    {
      name: 'Sell Orders',
      data: [...sellData],
      color: '#ef4444',
    },
  ];

  return <ReactApexChart options={options} series={series} type="area" height={350} />;
}

export default MarketDepthChart;
